import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { Components } from './styled';
import ProjectModal from '../../ProjectModal';
import { textFieldUtils } from '../../../utils/textFields';
import ProjectTextField from '../../inputs/ProjectTextField';
import ProjectSelect from '../../inputs/ProjectSelect';
import { Formik, FormikValues } from 'formik';
import { ActivityIndicatorContext } from '../../../providers/ActivityIndicatorProvider/context';
import { AppSettingsContext } from '../../../providers/AppSettingsProvider/context';
import useModalState from '../../../hooks/useModalState';
import ConfirmationModal from '../ConfirmationModal';
import { confirmationTexts } from '../ConfirmationModal/utils';
import { getDisplayNames } from '../../../api/utils';
import { Section, Tab, TabType } from '../../../api/appConfiguration/types';
import { CELL_TYPE, FETCH_RULE_TYPES, FetchRuleType, SECTION_TYPE, SEE_ALL_CELL_TYPE } from '../../../api/types/others';
import { capitalizeFirstLetter, generateRandomKey } from '../../../utils/strings';
import { SectionSchema } from '../../../api/appConfiguration/parser';
import ProjectTable from '../../ProjectTable';
import { tableColumns } from '../../ProjectTable/constants';
import { ContentGroup } from '../../../api/types/contentGroup';
import { getContentGroupsByIds } from '../../../api/contentGroup';
import ProjectTableModal from '../../ProjectTableModal';
import { modalConfigs } from '../../ProjectTableModal/constants';
import { ProjectTableDataType } from '../../ProjectTable/types';
import { ProjectErrors, ProjectErrorType } from '../../../utils/errors/types';
import CellTypePreview from './components/CellTypePreview';
import {
  sectionTypesWithFetchRule,
  sectionTypesWithSeeAllConfig,
  sectionTypesWithStyle,
  sectionTypesWithVisibleItems,
} from './utils';
import { canDeleteOrChangeSection } from '../../../screens/AppConfiguration/utils';
import { ToastContext } from '../../../providers/ToastProvider/context';
import { getProjectErrorForCode } from '../../../utils/errors';
import { Goal } from '../../../api/types/contentManagement';
import { getAllGoals } from '../../../api/contentManagement';
import CheckboxComponent from './components/CheckboxComponent';
import { LARGE_PROJECT_MODAL_WIDTH } from '../../../utils/styled/dimensions';

const initialValues = {
  key: '',
  title: '',
  type: '',
  style: '',
  sectionVisibleItems: '',
  pagination: '',
  seeAllConfigTitle: '',
  seeAllConfigStyle: '',
  fetchRuleType: '',
  fetchRuleCategory: '',
  fetchRulePillarType: '',
  fetchRulePillarExpertise: '',
  fetchRuleAuthors: [],
  fetchRuleContentGroupIds: [],
  placeholderText: '',
  forceSeeAll: false,
  showInLoop: false,
  isTestingSection: false,
  removeFromSale: false,
  showAuthorCategoriesOrderTab: false,
  description: '',
  tags: [],
};

const ModalBody = ({
  modalState,
  onSave,
  onCancel,
  contentGroups,
  setContentGroups,
  goals,
  setGoals,
  addCollectionsModalProps,
  addBundlesModalProps,
  addGoalsModalProps,
}: {
  modalState?: { section?: Section; tab?: TabType; appConfiguration?: Tab[] };
  onSave: (values: FormikValues) => Promise<void>;
  onCancel: () => void;
  contentGroups: ContentGroup[];
  setContentGroups: Dispatch<SetStateAction<ContentGroup[]>>;
  goals: Goal[];
  setGoals: Dispatch<SetStateAction<Goal[]>>;
  addCollectionsModalProps: any;
  addBundlesModalProps: any;
  addGoalsModalProps: any;
}) => {
  const { categories, pillarTypes, pillarExpertises } = useContext(AppSettingsContext);
  const { showErrorToast } = useContext(ToastContext);

  useEffect(() => {
    if (!modalState?.section) {
      setGoals([]);
    }

    if (modalState?.section?.fetchRule?.ids && modalState?.section?.fetchRule?.type !== 'authors') {
      if (modalState?.section?.type === 'goalsList') {
        getAllGoals().then((res) => {
          setGoals(
            modalState?.section?.fetchRule?.ids?.map((id: string) => res.filter((goal) => goal?.id === id)?.[0]),
          );
        });
      } else {
        getContentGroupsByIds(modalState?.section?.fetchRule?.ids).then((res) => {
          setContentGroups(res);
        });
      }
    }
  }, []);

  const sectionInitialValues = {
    key: modalState?.section?.key,
    title: modalState?.section?.title,
    type: modalState?.section?.type,
    style:
      modalState?.section?.style?.cellType === 'simpleCard' && modalState?.section?.style?.isVertical
        ? 'twoColumnsList'
        : modalState?.section?.style?.cellType,
    sectionVisibleItems: modalState?.section?.sectionVisibleItems?.toString(),
    pagination: modalState?.section?.pagination?.size?.toString(),
    seeAllConfigTitle: modalState?.section?.seeAllConfig?.title,
    seeAllConfigStyle:
      modalState?.section?.seeAllConfig?.cellType === 'simpleCard' && modalState?.section?.seeAllConfig?.isVertical
        ? 'twoColumnsList'
        : modalState?.section?.seeAllConfig?.cellType,
    fetchRuleType: modalState?.section?.type === 'collectionSection' ? 'static' : modalState?.section?.fetchRule?.type,
    fetchRuleCategory: modalState?.section?.fetchRule?.category,
    fetchRulePillarType: modalState?.section?.fetchRule?.pillar,
    fetchRulePillarExpertise: modalState?.section?.fetchRule?.pillarExpertise,
    fetchRuleAuthors: modalState?.section?.fetchRule?.ids,
    fetchRuleContentGroupIds: modalState?.section?.fetchRule?.ids,
    placeholderText: modalState?.section?.placeholderText,
    forceSeeAll: modalState?.section?.forceSeeAll || false,
    showInLoop: modalState?.section?.showInLoop || false,
    isTestingSection: modalState?.section?.isTestingSection || false,
    removeFromSale: modalState?.section?.removeFromSale || false,
    showAuthorCategoriesOrderTab: modalState?.section?.showAuthorCategoriesOrderTab || false,
    description: modalState?.section?.description || '',
    tags: modalState?.section?.tags || [],
  };

  const _onSubmit = async (values: FormikValues) => {
    await onSave?.({
      ...values,
    });
  };

  const _onAdd = (values: FormikValues) => {
    if (values.type === 'collectionList' || values.type === 'collectionSection') {
      addCollectionsModalProps.open({});
    } else if (values.type === 'bundleList') {
      addBundlesModalProps.open({});
    } else if (values.type === 'goalsList') {
      addGoalsModalProps.open();
    }
  };

  const onRemoveContentGroup = (id: string) => {
    setContentGroups((prev) => prev.filter((item) => item.id !== id));
  };
  const onRemoveGoal = (id: string) => {
    setGoals((prev) => prev.filter((item) => item.id !== id));
  };

  const moveItem = (id: string, direction: 'up' | 'down', array: any[]) => {
    const index = array.findIndex((item) => item.id === id);
    if (index < 0) {
      return;
    }

    const newArray = [...array];
    const temp = newArray[index];
    if (direction === 'up' && index > 0) {
      newArray[index] = newArray[index - 1];
      newArray[index - 1] = temp;
    } else if (direction === 'down' && index < newArray.length - 1) {
      newArray[index] = newArray[index + 1];
      newArray[index + 1] = temp;
    }

    return newArray;
  };

  const onMoveContentGroupUp = (id: string) => {
    const result = moveItem(id, 'up', contentGroups);
    setContentGroups(result as ContentGroup[]);
  };
  const onMoveContentGroupDown = (id: string) => {
    const result = moveItem(id, 'down', contentGroups);
    setContentGroups(result as ContentGroup[]);
  };

  const onMoveGoalUp = (id: string) => {
    const result = moveItem(id, 'up', goals);
    setGoals(result as Goal[]);
  };
  const onMoveGoalDown = (id: string) => {
    const result = moveItem(id, 'down', goals);
    setGoals(result as Goal[]);
  };

  return (
    <Formik
      initialValues={modalState ? sectionInitialValues : initialValues}
      validationSchema={SectionSchema}
      onSubmit={_onSubmit}>
      {({ values, handleChange, errors, touched }) => (
        <Components.Form>
          <Components.ModalBodyContainer>
            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'minimal'} />
              <Components.FieldsContent>
                <Components.FieldLabel>Type</Components.FieldLabel>
                <ProjectSelect
                  value={values.type}
                  processShownLabel={getDisplayNames.sectionType}
                  onChangeSelectedItem={(e) => {
                    const check = canDeleteOrChangeSection({
                      appConfiguration: modalState?.appConfiguration,
                      section: modalState?.section,
                    });
                    if (check) {
                      handleChange(e);
                    } else {
                      showErrorToast(getProjectErrorForCode(ProjectErrors.cannotChangeSection));
                    }
                  }}
                  name={'type'}
                  errorMessage={errors.type && touched.type ? errors?.type : undefined}
                  placeholder={'Select section type'}>
                  {SECTION_TYPE.map((key) => (
                    <Components.MenuItem key={key} value={key}>
                      {getDisplayNames.sectionType(key)}
                    </Components.MenuItem>
                  ))}
                  )
                </ProjectSelect>
              </Components.FieldsContent>
            </Components.FieldsWrapper>

            <Components.FieldsWrapper>
              <Components.FieldStartAdornment variant={'additional'} />
              <Components.FieldsContent>
                <Components.FieldWrapper>
                  <Components.FieldLabel>Title</Components.FieldLabel>
                  <ProjectTextField
                    value={values.title}
                    onChangeText={handleChange}
                    name={'title'}
                    placeholder={'Enter section title'}
                  />
                </Components.FieldWrapper>

                {values.type && sectionTypesWithVisibleItems.includes(values.type) && (
                  <Components.FieldWrapper>
                    <Components.FieldLabel>Visible Items</Components.FieldLabel>
                    <Components.LengthInputsWrapper>
                      <ProjectTextField
                        value={values.sectionVisibleItems}
                        onChangeText={handleChange}
                        name={'sectionVisibleItems'}
                        placeholder={'Enter the maximum number of visible items'}
                        {...textFieldUtils.inputPositiveNumber}
                      />
                    </Components.LengthInputsWrapper>
                  </Components.FieldWrapper>
                )}

                {values.type && sectionTypesWithStyle.includes(values.type) && (
                  <Components.FieldsContent>
                    {values.style && <CellTypePreview type={values.style} />}
                    <Components.FieldLabel>Style</Components.FieldLabel>
                    <ProjectSelect
                      value={values.style}
                      processShownLabel={getDisplayNames.cellType}
                      onChangeSelectedItem={handleChange}
                      name={'style'}
                      placeholder={'Select section style'}>
                      {CELL_TYPE.map((key) => (
                        <Components.MenuItem key={key} value={key}>
                          {getDisplayNames.cellType(key)}
                        </Components.MenuItem>
                      ))}
                      )
                    </ProjectSelect>
                  </Components.FieldsContent>
                )}

                {values.type && sectionTypesWithSeeAllConfig.includes(values.type) && (
                  <>
                    <Components.FieldWrapper>
                      <Components.FieldLabel>{'"See All" Title'}</Components.FieldLabel>
                      <ProjectTextField
                        value={values.seeAllConfigTitle}
                        onChangeText={handleChange}
                        name={'seeAllConfigTitle'}
                        placeholder={'Enter "See All" title'}
                      />
                    </Components.FieldWrapper>
                    <Components.FieldsContent>
                      <Components.FieldLabel>{'"See All" Style'}</Components.FieldLabel>
                      <ProjectSelect
                        value={values.seeAllConfigStyle}
                        processShownLabel={getDisplayNames.cellType}
                        onChangeSelectedItem={handleChange}
                        name={'seeAllConfigStyle'}
                        placeholder={'Select "See All" style'}>
                        {SEE_ALL_CELL_TYPE.map((key) => (
                          <Components.MenuItem key={key} value={key}>
                            {getDisplayNames.seeAllCellType(key)}
                          </Components.MenuItem>
                        ))}
                        )
                      </ProjectSelect>
                    </Components.FieldsContent>
                  </>
                )}

                {values.type && sectionTypesWithFetchRule.includes(values.type) && (
                  <>
                    <Components.FieldsContent>
                      <Components.FieldLabel>Fetch Rule</Components.FieldLabel>
                      <ProjectSelect
                        value={values.fetchRuleType}
                        processShownLabel={getDisplayNames.fetchRuleType}
                        onChangeSelectedItem={handleChange}
                        name={'fetchRuleType'}
                        placeholder={'Select fetch rule type'}>
                        {FETCH_RULE_TYPES.map((key) => (
                          <Components.MenuItem key={key} value={key}>
                            {getDisplayNames.fetchRuleType(key)}
                          </Components.MenuItem>
                        ))}
                        )
                      </ProjectSelect>
                    </Components.FieldsContent>

                    <Components.FieldWrapper>
                      <Components.FieldLabel>Page size</Components.FieldLabel>
                      <Components.LengthInputsWrapper>
                        <ProjectTextField
                          value={values.pagination}
                          onChangeText={handleChange}
                          name={'pagination'}
                          placeholder={'Enter the page size'}
                          {...textFieldUtils.inputPositiveNumber}
                        />
                      </Components.LengthInputsWrapper>
                    </Components.FieldWrapper>
                  </>
                )}

                {values.fetchRuleType === 'category' && (
                  <Components.FieldsContent>
                    <Components.FieldLabel>Category</Components.FieldLabel>
                    <ProjectSelect
                      value={values.fetchRuleCategory}
                      onChangeSelectedItem={handleChange}
                      // @ts-ignore
                      errorMessage={
                        errors.fetchRuleCategory && touched.fetchRuleCategory ? errors?.fetchRuleCategory : undefined
                      }
                      name={'fetchRuleCategory'}
                      placeholder={'Select a category'}>
                      {categories.map((item, key) => (
                        <Components.MenuItem key={key} value={item}>
                          {item}
                        </Components.MenuItem>
                      ))}
                      )
                    </ProjectSelect>
                  </Components.FieldsContent>
                )}

                {values.fetchRuleType === 'pillar' && (
                  <>
                    <Components.FieldsContent>
                      <Components.FieldLabel>Pillar Type</Components.FieldLabel>
                      <ProjectSelect
                        value={values.fetchRulePillarType}
                        processShownLabel={capitalizeFirstLetter}
                        onChangeSelectedItem={handleChange}
                        // @ts-ignore
                        errorMessage={
                          errors.fetchRulePillarType && touched.fetchRulePillarType
                            ? errors.fetchRulePillarType
                            : undefined
                        }
                        name={'fetchRulePillarType'}
                        placeholder={'Select a pillar type'}>
                        {pillarTypes.map((item, key) => (
                          <Components.MenuItem key={key} value={item}>
                            {capitalizeFirstLetter(item)}
                          </Components.MenuItem>
                        ))}
                        )
                      </ProjectSelect>
                    </Components.FieldsContent>
                    <Components.FieldsContent>
                      <Components.FieldLabel>Pillar Expertise</Components.FieldLabel>
                      <ProjectSelect
                        value={values.fetchRulePillarExpertise}
                        processShownLabel={capitalizeFirstLetter}
                        onChangeSelectedItem={handleChange}
                        name={'fetchRulePillarExpertise'}
                        placeholder={'Select a pillar expertise'}>
                        {pillarExpertises.map((item, key) => (
                          <Components.MenuItem key={key} value={item}>
                            {capitalizeFirstLetter(item)}
                          </Components.MenuItem>
                        ))}
                        )
                      </ProjectSelect>
                    </Components.FieldsContent>
                  </>
                )}

                {(values.fetchRuleType === 'static' || values.type === 'collectionSection') && (
                  <Components.FieldWrapper>
                    <Components.FieldLabel>
                      {values.type === 'collectionList' || values.type === 'collectionSection'
                        ? 'Collections'
                        : 'Bundles'}
                      <Components.AddButton onClick={() => _onAdd(values)}>
                        Add{' '}
                        {values.type === 'collectionList' || values.type === 'collectionSection'
                          ? 'Collections'
                          : 'Bundles'}
                      </Components.AddButton>
                    </Components.FieldLabel>
                    <Components.TableWrapper>
                      {values.type === 'collectionList' || values.type === 'collectionSection' ? (
                        <ProjectTable
                          data={contentGroups || []}
                          columns={tableColumns.collectionsInSection({
                            onRemove: onRemoveContentGroup,
                            onMoveUp: onMoveContentGroupUp,
                            onMoveDown: onMoveContentGroupDown,
                          })}
                        />
                      ) : (
                        <ProjectTable
                          data={contentGroups || []}
                          columns={tableColumns.bundlesInSection({
                            onRemove: onRemoveContentGroup,
                            onMoveUp: onMoveContentGroupUp,
                            onMoveDown: onMoveContentGroupDown,
                          })}
                        />
                      )}
                    </Components.TableWrapper>
                  </Components.FieldWrapper>
                )}

                {values.type === 'goalsList' && (
                  <Components.FieldWrapper>
                    <Components.FieldLabel>
                      Goals
                      <Components.AddButton onClick={() => _onAdd(values)}>Add goals</Components.AddButton>
                    </Components.FieldLabel>
                    {goals.length > 0 ? (
                      <Components.TableWrapper>
                        <ProjectTable
                          data={goals || []}
                          columns={tableColumns.goalsInSections({
                            onRemove: onRemoveGoal,
                            onMoveUp: onMoveGoalUp,
                            onMoveDown: onMoveGoalDown,
                          })}
                        />
                      </Components.TableWrapper>
                    ) : (
                      <>
                        <Components.BasicText>
                          All goals will be shown in-app by default.
                          <br />
                          Click on {'"'}Add goals{'"'} to select a subset of goals that should be visible for this
                          section.
                        </Components.BasicText>
                      </>
                    )}
                  </Components.FieldWrapper>
                )}

                <Components.FieldWrapper>
                  <Components.FieldLabel>{'Placeholder'}</Components.FieldLabel>
                  <ProjectTextField
                    value={values.placeholderText}
                    onChangeText={handleChange}
                    name={'placeholderText'}
                    placeholder={'Enter custom placeholder text'}
                  />
                  <Components.BasicText>
                    If you fill in the placeholder, this section will be visible in-app even if it were empty (in which
                    case this text would be visible instead). Do not fill this in if you want the section to be hidden
                    when no content is found within it.
                  </Components.BasicText>
                </Components.FieldWrapper>

                <Components.FieldWrapper>
                  <Components.FieldLabel>Description</Components.FieldLabel>
                  <ProjectTextField
                    value={values.description}
                    onChangeText={handleChange}
                    name={'description'}
                    placeholder={'Enter a short description'}
                  />
                </Components.FieldWrapper>

                <Components.FieldWrapper>
                  <Components.FieldLabel>Tags</Components.FieldLabel>
                  <Components.TagsContainer>
                    {values.tags?.map((tag: string, index: number) => (
                      <Components.Tag key={index}>
                        {tag}
                        <Components.RemoveTagButton
                          onClick={() => {
                            const newTags = [...(values.tags || [])];
                            newTags.splice(index, 1);
                            handleChange({
                              target: {
                                name: 'tags',
                                value: newTags,
                              },
                            });
                          }}>
                          ×
                        </Components.RemoveTagButton>
                      </Components.Tag>
                    ))}
                    <Components.TagInput
                      placeholder='Add a tag and press Enter'
                      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                          e.preventDefault();
                          const newTag = e.currentTarget.value.trim();
                          if (!values.tags?.includes(newTag)) {
                            handleChange({
                              target: {
                                name: 'tags',
                                value: [...(values.tags || []), newTag],
                              },
                            });
                          }
                          e.currentTarget.value = '';
                        }
                      }}
                    />
                  </Components.TagsContainer>
                </Components.FieldWrapper>

                <Components.FieldWrapper>
                  <Components.FieldLabel>Special section tags</Components.FieldLabel>
                </Components.FieldWrapper>

                <CheckboxComponent
                  checked={values.forceSeeAll}
                  onChange={(e) => {
                    handleChange(e);
                  }}
                  name={'forceSeeAll'}
                  label={'Force see all'}
                />
                <CheckboxComponent
                  checked={values.isTestingSection}
                  onChange={(e) => {
                    handleChange(e);
                  }}
                  name={'isTestingSection'}
                  label={'Is testing section'}
                />
                <CheckboxComponent
                  checked={values.removeFromSale}
                  onChange={(e) => {
                    handleChange(e);
                  }}
                  name={'removeFromSale'}
                  label={'Remove from sale'}
                />
                <CheckboxComponent
                  checked={values.showInLoop}
                  onChange={(e) => {
                    handleChange(e);
                  }}
                  name={'showInLoop'}
                  label={'Show in loop'}
                />
                {values.fetchRuleType === 'authors' && (
                  <CheckboxComponent
                    checked={values.showAuthorCategoriesOrderTab}
                    onChange={(e) => {
                      handleChange(e);
                    }}
                    name={'showAuthorCategoriesOrderTab'}
                    label={'Show author categories order tab'}
                  />
                )}
              </Components.FieldsContent>
            </Components.FieldsWrapper>
          </Components.ModalBodyContainer>
          <Components.ModalButtonsContainer>
            <Components.ModalTertiaryButton onClick={() => onCancel?.()}>Cancel</Components.ModalTertiaryButton>
            <Components.ModalPrimaryButton type={'submit'}>Save</Components.ModalPrimaryButton>
          </Components.ModalButtonsContainer>
        </Components.Form>
      )}
    </Formik>
  );
};

const CreateOrEditSectionModal = ({
  isModalOpen,
  onModalClose,
  modalState,
  refreshCallback,
  onCreateOrUpdateSection,
}: {
  isModalOpen: boolean;
  onModalClose: (refreshTrigger?: boolean) => void;
  modalState?: { section?: Section; tab?: TabType; appConfiguration?: Tab[] };
  refreshCallback?: () => Promise<void>;
  onCreateOrUpdateSection: (section: Section) => Promise<ProjectErrorType | void>;
}) => {
  const { setIsLoading } = useContext(ActivityIndicatorContext);
  const { authors } = useContext(AppSettingsContext);

  const addCollectionsModalProps = useModalState<{}>();
  const addBundlesModalProps = useModalState<{}>();
  const addGoalsModalProps = useModalState<{}>();
  const confirmationModal = useModalState<boolean>();

  const [contentGroups, setContentGroups] = useState<ContentGroup[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);

  const _onModalClose = (refreshTrigger?: boolean) => {
    confirmationModal.open(refreshTrigger || false);
  };

  const _processFetchRuleForType = (values: FormikValues, type: FetchRuleType) => {
    return {
      type,
      ...(type === 'category' && { category: values.fetchRuleCategory }),
      ...(type === 'authors' && {
        ids: [],
      }),
      ...(type === 'pillar' && {
        pillar: values.fetchRulePillarType,
        ...(values.fetchRulePillarExpertise && { pillarExpertise: values.fetchRulePillarExpertise }),
      }),
      ...(type === 'static' && { ids: contentGroups.map((contentGroup) => contentGroup.id) }),
    };
  };

  const onAddContentGroups = (data: ProjectTableDataType | string[]) => {
    const items = data as ContentGroup[];
    setContentGroups((prev) => [...prev, ...items]);
    if (addCollectionsModalProps.isModalOpen) {
      addCollectionsModalProps.onModalClose();
    } else {
      addBundlesModalProps.onModalClose();
    }
  };

  const onAddGoals = (data: ProjectTableDataType | string[]) => {
    const items = goals.concat(data as Goal[]);
    setGoals(items);
    addGoalsModalProps.onModalClose();
  };

  const _onSave = async (values: FormikValues) => {
    const data: Section = {
      key: values.key || generateRandomKey(20),
      type: values.type,
      pagination: {
        ...(values.pagination && { size: parseInt(values.pagination) }),
      },
      ...(values.title && { title: values.title }),
      ...(values.style && {
        style: {
          cellType: values.style === 'twoColumnsList' ? 'simpleCard' : values.style,
          ...(values.style === 'twoColumnsList' && { isVertical: true }),
        },
      }),
      ...(values.fetchRuleType && { fetchRule: _processFetchRuleForType(values, values.fetchRuleType) }),
      ...(values.sectionVisibleItems && { sectionVisibleItems: parseInt(values.sectionVisibleItems) }),
      ...((values.seeAllConfigTitle || values.seeAllConfigStyle) && {
        seeAllConfig: {
          ...(values.seeAllConfigTitle && { title: values.seeAllConfigTitle }),
          ...(values.seeAllConfigStyle && {
            cellType: values.seeAllConfigStyle === 'twoColumnsList' ? 'simpleCard' : values.seeAllConfigStyle,
            ...(values.seeAllConfigStyle === 'twoColumnsList' && { isVertical: true }),
          }),
        },
      }),
      ...(values?.type === 'goalsList' && { fetchRule: { ids: goals?.map((item) => item?.id) } }),
      ...(values?.placeholderText?.trim()?.length > 0 && { placeholderText: values?.placeholderText?.trim() }),
      ...(values?.forceSeeAll && { forceSeeAll: values?.forceSeeAll }),
      ...(values?.showInLoop && { showInLoop: values?.showInLoop }),
      ...(values?.isTestingSection && { isTestingSection: values?.isTestingSection }),
      ...(values?.removeFromSale && { removeFromSale: values?.removeFromSale }),
      ...(values?.showAuthorCategoriesOrderTab && {
        showAuthorCategoriesOrderTab: values?.showAuthorCategoriesOrderTab,
      }),
      ...(values?.description && { description: values?.description }),
      ...(values?.tags && { tags: values?.tags }),
    };

    setIsLoading(true);
    await onCreateOrUpdateSection(data);
    setIsLoading(false);

    onModalClose?.();
    refreshCallback?.().then();
  };

  const onConfirmCancel = (value: boolean) => {
    onModalClose?.(value);
  };

  return (
    <>
      <ProjectModal
        bodyChildren={
          <ModalBody
            modalState={modalState}
            onSave={_onSave}
            onCancel={_onModalClose}
            contentGroups={contentGroups}
            setContentGroups={setContentGroups}
            goals={goals}
            setGoals={setGoals}
            addCollectionsModalProps={addCollectionsModalProps}
            addBundlesModalProps={addBundlesModalProps}
            addGoalsModalProps={addGoalsModalProps}
          />
        }
        isModalOpen={isModalOpen}
        onModalClose={_onModalClose}
        modalTitle={modalState?.section ? `Edit section` : 'Create a new section'}
        titleSuffix={modalState?.section ? `(ID: ${modalState?.section?.key})` : undefined}
        disableCloseOnClickAway
        modalWidth={LARGE_PROJECT_MODAL_WIDTH}
      />
      <ConfirmationModal
        {...confirmationModal}
        confirmationText={confirmationTexts.sections.cancelEditing}
        onConfirm={onConfirmCancel}
      />
      <ProjectTableModal
        modalConfig={modalConfigs.selectCollections({})}
        {...addCollectionsModalProps}
        modalTitle={'Select collections to add in this section'}
        ignoredIds={contentGroups.map((contentGroup) => contentGroup.id)}
        onModalSubmit={onAddContentGroups}
        modalWidth={LARGE_PROJECT_MODAL_WIDTH}
      />
      <ProjectTableModal
        modalConfig={modalConfigs.selectBundles({})}
        {...addBundlesModalProps}
        modalTitle={'Select bundles to add in this section'}
        ignoredIds={contentGroups.map((contentGroup) => contentGroup.id)}
        onModalSubmit={onAddContentGroups}
        modalWidth={LARGE_PROJECT_MODAL_WIDTH}
      />
      <ProjectTableModal
        modalConfig={modalConfigs.selectGoals}
        {...addGoalsModalProps}
        modalTitle={'Select goals to add in this section'}
        ignoredIds={goals.map((goal) => goal?.id)}
        onModalSubmit={onAddGoals}
        modalWidth={LARGE_PROJECT_MODAL_WIDTH}
      />
    </>
  );
};

export default CreateOrEditSectionModal;
